import { CircularProgress, Link, LinkProps, styled } from "@mui/material";
import { Map } from "leaflet";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "notistack";

import { useDeleteMeasureConfiguration } from "hooks/measureConfigHooks";

import { MeasureConf } from "types/types";
import {
  applyDeleteLayerStyle,
  applyPointLayerStyle,
  applyPolygonLayerStyle,
} from "utils/GeomanUtils";

interface Props extends LinkProps {
  mapRef: Map;
  measureConfig: MeasureConf;
  measureConfigs: MeasureConf[];
  uuid: string;
}

export function RemoveMeasureLink(props: Props) {
  const { mapRef, uuid, measureConfig, measureConfigs, ...linkProps } = props;
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const { mutateAsync: deleteMeasureConf, isLoading } =
    useDeleteMeasureConfiguration(uuid);

  async function deleteMeasureConfig(measureConfigId: number) {
    await deleteMeasureConf(measureConfigId, {
      onSuccess: () => {
        enqueueSnackbar(t("label.measureDeleted"), { variant: "success" });
      },
    });
  }

  function removeLayers() {
    deleteMeasureConfig(measureConfig.id);
    measureConfig.layers.forEach((layer) => {
      mapRef.removeLayer(layer.geometryLayer);
    });
  }

  function removeLayerStyles() {
    measureConfig.layers.forEach((layer) => {
      if (measureConfig.measure.geometryType === "POLYGON") {
        return applyPolygonLayerStyle(layer.geometryLayer, measureConfig);
      }
      applyPointLayerStyle(layer.geometryLayer, measureConfig);
    });
  }

  function setDeleteLayerStyles() {
    measureConfig.layers.forEach((layer) => {
      applyDeleteLayerStyle(layer.geometryLayer);
    });
  }

  return (
    <StyledLink
      {...linkProps}
      onMouseEnter={setDeleteLayerStyles}
      onMouseLeave={removeLayerStyles}
      onClick={removeLayers}
    >
      {!isLoading && t("label.delete")}
      {isLoading && <CircularProgress size={15} />}
    </StyledLink>
  );
}

const StyledLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: ${({ theme }) => theme.spacing(8)};
`;
